import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Link } from 'react-router-dom';
import './App.css';
import * as db from './services/db';
import { User } from './types';
import LoginScreen from './components/LoginScreen';
import DeviceMode from './components/DeviceMode';
import Bill from './pages/Bill';
import Products from './pages/Products';
import Customers from './pages/Customers';
import Reports from './pages/Reports';
import HeadOffice from './pages/HeadOffice';
import Settings from './pages/Settings';
import GoodsReceived from './pages/GoodsReceived';
import StockOut from './pages/StockOut';
import CashFlow from './pages/CashFlow';
import EditBill from './pages/EditBill';
import SaleReturn from './pages/SaleReturn';
import OrderRequest from './pages/OrderRequest';
import BarcodePrinting from './pages/BarcodePrinting';
import BarcodeSettings from './pages/BarcodeSettings';
import PrintSettings from './pages/PrintSettings';
import AppSettings from './pages/AppSettings';
import Shifts from './pages/Shifts';
import ShiftEnd from './pages/ShiftEnd';
import OpeningBalance from './pages/OpeningBalance';
import DReports from './pages/DReports';
import EReports from './pages/EReports';
import NgstSummary from './pages/NgstSummary';
import GstReports from './pages/GstReports';
import ProductSaleReport from './pages/ProductSaleReport';
import TransportReport from './pages/TransportReport';
import ExcelData from './pages/ExcelData';
import Suppliers from './pages/Suppliers';
import Categories from './pages/Categories';
import Units from './pages/Units';
import Attributes from './pages/Attributes';
import AuditLog from './pages/AuditLog';
import Users from './pages/Users';
import Rooms from './pages/Rooms';
import Salesmen from './pages/Salesmen';
import Recipes from './pages/Recipes';
import ServicePoints from './pages/ServicePoints';
import Coupons from './pages/Coupons';
import Discounts from './pages/Discounts';
import About from './pages/About';
import Licenses from './pages/Licenses';
import BackendStub from './pages/BackendStub';
import AppUpdate from './pages/AppUpdate';
import Rfid from './pages/Rfid';
import LedDisplay from './pages/LedDisplay';
import Dashboard from './pages/Dashboard';
import Tables from './pages/Tables';
import CaptainApp from './pages/CaptainApp';
import KitchenDisplay from './pages/KitchenDisplay';
import KotReport from './pages/KotReport';
import CustomerReport from './pages/CustomerReport';
import OnlineOrders from './pages/OnlineOrders';
import SwiggyZomato from './pages/SwiggyZomato';
import SmsConfig from './pages/SmsConfig';
import QrLogin from './pages/QrLogin';
import UsbLink from './pages/UsbLink';
import BluetoothPrinter from './pages/BluetoothPrinter';
import Navigation from './components/Navigation';
import BackButtonHandler from './components/BackButtonHandler';
import UsbPrinterPrompt from './components/UsbPrinterPrompt';
import LicenseGate from './components/LicenseGate';
import { PosProvider } from './context/PosContext';
import { maybeAutoBackup } from './services/folderBackup';

// Drives the periodic folder auto-backup (Android only). Checks once on launch
// and every 30 minutes thereafter; maybeAutoBackup itself enforces the chosen
// interval and is a no-op on the web or when auto-backup is off.
const AutoBackupRunner: React.FC = () => {
  useEffect(() => {
    maybeAutoBackup();
    const id = setInterval(() => {
      maybeAutoBackup();
    }, 30 * 60 * 1000);
    return () => clearInterval(id);
  }, []);
  return null;
};

// Storage-full guard: db.write fires 'sconnect:storage-full' when a save fails
// because the device localStorage quota is full (instead of failing silently).
// We surface one prompt offering to jump to Settings → Archive & Clear Old Bills.
// Bursts of failed writes are coalesced so the operator isn't spammed.
const StorageFullPrompt: React.FC = () => {
  const navigate = useNavigate();
  const lastShown = useRef(0);
  useEffect(() => {
    const onFull = () => {
      // At most one prompt per 15s, even if many writes fail in quick succession.
      if (Date.now() - lastShown.current < 15000) return;
      lastShown.current = Date.now();
      const go = window.confirm(
        'Device storage is full — your most recent change may not have been saved.\n\n' +
          'Archive and clear old bills to free space. Open Settings now?'
      );
      if (go) navigate('/settings');
    };
    window.addEventListener('sconnect:storage-full', onFull);
    return () => window.removeEventListener('sconnect:storage-full', onFull);
  }, [navigate]);
  return null;
};

// Login gate: inner pages require an operator login (Quick PIN) whenever
// active users exist. A fresh install with no users is let straight in so the
// owner can set up the first user, and logout anywhere relocks the app via
// the 'sconnect:auth' event fired by db.setCurrentUserId.
const LoginGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [hasUsers, setHasUsers] = useState(false);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let alive = true;
    const sync = async () => {
      const [u, users] = await Promise.all([db.getCurrentUser(), db.getUsers()]);
      if (!alive) return;
      setUser(u);
      setHasUsers(users.some((x) => x.active));
      setReady(true);
    };
    sync();
    window.addEventListener('sconnect:auth', sync);
    return () => {
      alive = false;
      window.removeEventListener('sconnect:auth', sync);
    };
  }, []);
  // Wait for the first load before deciding, so the login screen never flashes
  // for an operator who is actually logged in.
  if (!ready) return null;
  if (!hasUsers || (user && user.active)) return <>{children}</>;
  return <LoginScreen />;
};

// Terminal-mode gate: on first launch (no device mode chosen yet) show the
// picker full-screen so a new device starts on a limited menu instead of full
// admin. Once a mode is set, db.setDeviceRole fires 'sconnect:auth' and the
// gate hands through to the app. It can be re-opened later at /device-mode.
const DeviceModeGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [chosen, setChosen] = useState(db.isDeviceRoleChosen());
  useEffect(() => {
    const sync = () => setChosen(db.isDeviceRoleChosen());
    window.addEventListener('sconnect:auth', sync);
    return () => window.removeEventListener('sconnect:auth', sync);
  }, []);
  if (!chosen) return <DeviceMode firstRun />;
  return <>{children}</>;
};

// Page container. The billing screen (POS machine view) renders full-bleed —
// edge to edge, full height below the app bar — so the two-pane bill layout
// uses the whole display; every other page keeps the normal page padding.
const Main: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const fullbleed = pathname === '/' || pathname === '/led-display';
  // The POS billing screen ('/') hides the top app bar (see Navigation), so it
  // also reclaims the 56px the header normally reserves — handled by the
  // pos-headerless body class in App.css.
  const headerless = pathname === '/';
  // Lock document scrolling while the full-bleed POS screen is shown so the
  // billing view behaves like a fixed native app (no page bounce/overscroll).
  // Restored on navigation away so every other page scrolls normally.
  React.useEffect(() => {
    document.body.classList.toggle('pos-noscroll', fullbleed);
    document.body.classList.toggle('pos-headerless', headerless);
    return () => {
      document.body.classList.remove('pos-noscroll');
      document.body.classList.remove('pos-headerless');
    };
  }, [fullbleed, headerless]);
  return (
    <main className={`main-content${fullbleed ? ' fullbleed' : ''}`}>
      {children}
    </main>
  );
};

// Route gate for the role rights matrix (Users page → Roles & Rights):
// pages outside the current operator's role rights show a lock screen
// instead of rendering, even when reached by URL.
const AccessGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [roleLabel, setRoleLabel] = useState<string | undefined>();
  useEffect(() => {
    let alive = true;
    (async () => {
      const ok = await db.canAccess(location.pathname);
      if (ok) {
        if (alive) setAllowed(true);
        return;
      }
      const user = await db.getCurrentUser();
      const role = user ? (await db.getRoles()).find((r) => r.id === user.role) : null;
      if (alive) {
        setAllowed(false);
        setRoleLabel(role?.name ?? user?.role);
      }
    })();
    return () => {
      alive = false;
    };
  }, [location.pathname]);
  if (allowed === null) return null;
  if (allowed) return <>{children}</>;
  return (
    <div className="access-denied">
      <span className="access-denied-icon">🔒</span>
      <h2>Access restricted</h2>
      <p>
        The <strong>{roleLabel}</strong> role does not have rights to this page.
        Ask an admin to update Roles &amp; Rights on the Users page.
      </p>
      <Link to="/dashboard">Go to Dashboard</Link>
    </div>
  );
};

function App() {
  return (
    <PosProvider>
      <div className="App">
        <Router>
          <BackButtonHandler />
          <AutoBackupRunner />
          <StorageFullPrompt />
          <UsbPrinterPrompt />
          <LicenseGate>
          <DeviceModeGate>
          <LoginGate>
          <Navigation />
          <Main>
            <AccessGate>
            <Routes>
              {/* Home */}
              <Route path="/dashboard" element={<Dashboard />} />

              {/* Billing */}
              <Route path="/" element={<Bill />} />
              <Route path="/edit-bill" element={<EditBill />} />
              <Route path="/sale-return" element={<SaleReturn />} />

              {/* Inventory */}
              <Route path="/products" element={<Products />} />
              <Route path="/goods-received" element={<GoodsReceived />} />
              <Route path="/stock-out" element={<StockOut />} />
              <Route path="/order-request" element={<OrderRequest />} />
              <Route path="/suppliers" element={<Suppliers />} />
              <Route path="/categories" element={<Categories />} />
              <Route path="/units" element={<Units />} />
              <Route path="/attributes" element={<Attributes />} />
              <Route path="/audit-log" element={<AuditLog />} />
              <Route path="/users" element={<Users />} />
              <Route path="/rooms" element={<Rooms />} />
              <Route path="/salesmen" element={<Salesmen />} />
              <Route path="/recipes" element={<Recipes />} />
              <Route path="/service-points" element={<ServicePoints />} />
              <Route path="/coupons" element={<Coupons />} />
              <Route path="/discounts" element={<Discounts />} />

              {/* Money */}
              <Route path="/cashflow" element={<CashFlow />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/customer-report" element={<CustomerReport />} />

              {/* Reports */}
              <Route path="/reports" element={<Reports />} />
              <Route path="/head-office" element={<HeadOffice />} />
              <Route path="/d-reports" element={<DReports />} />
              <Route path="/e-reports" element={<EReports />} />
              <Route path="/ngst-summary" element={<NgstSummary />} />
              <Route path="/gst-reports" element={<GstReports />} />
              <Route path="/product-sale-report" element={<ProductSaleReport />} />

              {/* Restaurant */}
              <Route path="/tables" element={<Tables />} />
              <Route path="/captain" element={<CaptainApp />} />
              <Route path="/kitchen" element={<KitchenDisplay />} />
              <Route path="/kot-report" element={<KotReport />} />

              {/* Shifts */}
              <Route path="/shift-end" element={<ShiftEnd />} />
              <Route path="/opening-balance" element={<OpeningBalance />} />
              <Route path="/shifts" element={<Shifts />} />

              {/* Config */}
              <Route path="/barcode-printing" element={<BarcodePrinting />} />
              <Route path="/barcode-settings" element={<BarcodeSettings />} />
              <Route path="/print-settings" element={<PrintSettings />} />
              <Route path="/app-settings" element={<AppSettings />} />
              <Route path="/usb-link" element={<UsbLink />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/licenses" element={<Licenses />} />
              <Route path="/about" element={<About />} />
              <Route path="/device-mode" element={<DeviceMode />} />

              {/* Backend / hardware-dependent stubs (not functional in the local-first web port) */}
              <Route path="/online-orders" element={<OnlineOrders />} />
              <Route path="/swiggy-zomato" element={<SwiggyZomato />} />
              <Route
                path="/sms-config"
                element={<SmsConfig />}
              />
              <Route path="/app-update" element={<AppUpdate />} />
              <Route path="/bluetooth-printer" element={<BluetoothPrinter />} />
              <Route path="/rfid" element={<Rfid />} />
              <Route path="/led-display" element={<LedDisplay />} />
              <Route
                path="/qr-login"
                element={<QrLogin />}
              />
              <Route path="/transport-report" element={<TransportReport />} />
              <Route path="/excel-email" element={<ExcelData />} />
            </Routes>
            </AccessGate>
          </Main>
          </LoginGate>
          </DeviceModeGate>
          </LicenseGate>
        </Router>
      </div>
    </PosProvider>
  );
}

export default App;
